import { useState, type ReactNode } from 'react';
import { VestingBar } from './VestingBar.js';

export interface HeroCardMetaItem {
  readonly value: ReactNode;
  /** Tone marks special meaning. `pos` renders in `text-pos` (emerald). */
  readonly tone?: 'default' | 'pos' | 'neg';
}

export interface HeroCardStats {
  readonly weight: ReactNode;
  readonly gpu: ReactNode;
  readonly share: ReactNode;
}

export interface HeroCardProps {
  /** Uppercase micro-label above the balance. */
  readonly title: string;
  /** Balance in ngonka (BigInt-safe string). */
  readonly balanceNgonka: string;
  /** Optional spendable portion (defaults to balanceNgonka). */
  readonly spendableNgonka?: string;
  /** Optional vesting portion (defaults to "0"). */
  readonly vestingNgonka?: string;
  /** Caller-supplied formatter (returns "186,097.4400" without unit). */
  readonly formatNgonka: (ngonka: string) => string;
  /** Inline meta chips below the balance. */
  readonly meta?: readonly HeroCardMetaItem[];
  /** Optional miner stats for collapsible expand. */
  readonly stats?: HeroCardStats;
  /** Render skeleton in place of the balance. */
  readonly isLoading?: boolean;
  /**
   * Collapsible mode: chevron + click toggles expand of stats / vesting.
   * Defaults to false; when true and there's nothing to expand
   * (no stats, no vesting), chevron is hidden.
   */
  readonly collapsible?: boolean;
  readonly className?: string;
  /** Translated copy. */
  readonly labels?: {
    readonly vestingUnlocked?: string;
    readonly vestingInProgress?: string;
  };
}

const DEFAULT_LABELS = {
  vestingUnlocked: 'Unlocked',
  vestingInProgress: 'Vesting',
};

/**
 * Hero balance card used by Wallet and Miner tabs (Plan 2+).
 *
 * Visual: surface-2 background, large mono balance, optional meta row,
 * optional collapsible stats grid (Weight/GPU/Share), optional vesting
 * progress bar with unlocked/locked rows.
 *
 * Tokens: bg-surface-2, text-primary balance, text-tertiary labels,
 * pos for positive meta, font-mono tabular-nums for numbers.
 *
 * The old HeroCard (emerald gradient) and apps/web/.../miner/MinerHeroCard
 * are still used by existing screens. Plan 3 migrates them to V2.
 * Plan 4 deletes both old versions and renames V2 to HeroCard.
 */
export function HeroCard(props: HeroCardProps): ReactNode {
  const {
    title,
    balanceNgonka,
    spendableNgonka,
    vestingNgonka = '0',
    formatNgonka,
    meta,
    stats,
    isLoading = false,
    collapsible = false,
    className = '',
    labels,
  } = props;

  const effectiveLabels = { ...DEFAULT_LABELS, ...(labels ?? {}) };
  const spendable = spendableNgonka ?? balanceNgonka;
  const hasVesting = vestingNgonka !== '0' && BigInt(vestingNgonka) > 0n;
  const hasExpandable = stats !== undefined || hasVesting;
  const showChevron = collapsible && hasExpandable;

  const [expanded, setExpanded] = useState(false);

  const totalBig = BigInt(balanceNgonka || '0');
  const spendableBig = BigInt(spendable || '0');

  return (
    <div
      className={[
        'rounded-[18px] bg-surface-2 px-5 py-5',
        showChevron && 'cursor-pointer',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={showChevron ? () => setExpanded((v) => !v) : undefined}
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
          {title}
        </div>
        {showChevron && (
          <button
            type="button"
            aria-label={expanded ? 'Collapse hero card' : 'Expand hero card'}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded((v) => !v);
            }}
            className="text-text-tertiary transition-transform duration-base ease-standard"
            style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        )}
      </div>

      <div className="mt-2 leading-none">
        {isLoading ? (
          <div
            data-skeleton
            className="h-[36px] w-48 animate-pulse rounded-md bg-surface-3"
          />
        ) : (
          <span className="font-mono text-[30px] font-medium tracking-[-1.2px] text-text-primary tabular-nums">
            {formatNgonka(balanceNgonka)}
            <span className="ml-1.5 font-sans text-[13px] font-normal tracking-normal text-text-tertiary">
              GNK
            </span>
          </span>
        )}
      </div>

      {meta && meta.length > 0 && (
        <div className="mt-2 flex items-center gap-2 text-[11px] text-text-secondary">
          {meta.map((item, idx) => (
            <span key={idx} className="flex items-center gap-2">
              {idx > 0 && (
                <span aria-hidden className="h-[3px] w-[3px] rounded-full bg-text-disabled" />
              )}
              <span
                className={[
                  'font-mono tabular-nums',
                  item.tone === 'pos' && 'text-pos',
                  item.tone === 'neg' && 'text-neg',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {item.value}
              </span>
            </span>
          ))}
        </div>
      )}

      {expanded && stats && (
        <div className="mt-4 grid grid-cols-3 gap-3 border-t border-border-hairline pt-4">
          <HeroStat label="Weight" value={stats.weight} />
          <HeroStat label="GPU" value={stats.gpu} />
          <HeroStat label="Share" value={stats.share} />
        </div>
      )}

      {expanded && hasVesting && (
        <div className="mt-4 border-t border-border-hairline pt-4">
          <VestingBar
            unlocked={
              totalBig === 0n ? 0 : Number((spendableBig * 10000n) / totalBig) / 10000
            }
            unlockedAmount={formatNgonka(spendable)}
            vestingAmount={formatNgonka(vestingNgonka)}
            labels={{
              unlocked: effectiveLabels.vestingUnlocked,
              vesting: effectiveLabels.vestingInProgress,
            }}
            className="mt-0"
          />
        </div>
      )}
    </div>
  );
}

function HeroStat({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div>
      <div className="text-[9px] font-semibold uppercase tracking-[1.2px] text-text-tertiary">
        {label}
      </div>
      <div className="mt-1 font-mono text-sm font-medium tabular-nums text-text-primary">
        {value}
      </div>
    </div>
  );
}
